import * as React from "react";
import { useEffect, useState, useMemo } from "react";

import { FiasExtendFieldsType, FiasSourceValue, ParsedExtendedFiasField } from "../../../FiasInput/Types/FiasTypes";
import { getHouseTypeShortName } from "../../../FiasInput/Utils/Convertations/House/House";
import { dirtyReduxHack, FiasAddress } from "../../../Utils/FiasAddressUtils";
import { AbbreviationTypes } from "../../../FiasInput/Types/AbbreviationSize";
import { useMetrics } from "../../../../../Internals/Metrics/MetricsProviderContext";
import { FIAS_HANDLE_EDIT_MODE_VALUE_CHANGE } from "../../../../../metricsAction";
import { Root } from "../FiasFnsManualEditor.styles";
import { FiasApi } from "../../../FiasInput/FiasApi";
import { FiasFnsFields } from "../../../FiasContainer";
import { If } from "../../../../../Commons/Components/ControlFlow/If";
import { useTrackFiasModalEnter } from "../../../FiasMetrics";

import { Guid } from "./FiasFnsManualEditorGuid";
import { Region } from "./ModalRows/Region";
import { District } from "./ModalRows/District";
import { Settlement } from "./ModalRows/Settlement";
import { City } from "./ModalRows/City";
import { Structure } from "./ModalRows/Structure";
import { Street } from "./ModalRows/Street";
import { Stead } from "./ModalRows/Stead";
import { House } from "./ModalRows/House";
import { Housing } from "./ModalRows/Housing";
import { Building } from "./ModalRows/Building";
import { Apartment } from "./ModalRows/Apartment";
import { Room } from "./ModalRows/Room";
import { Control, ControlApiPath, controlsApiPaths, defaultPaths } from "./controlsPaths";
import { Index } from "./ModalRows/Index";

interface FiasFnsManualEditorProps {
    value: FiasAddress;
    onValueChange: (nextValue: FiasAddress, guid?: string) => void;
    withSteads: boolean;
    abbreviationSize: AbbreviationTypes;
    withFederalTerritory?: boolean;
    withIndex: boolean;
    apiClient: FiasApi;
    extendedFields: ParsedExtendedFiasField[];
    hiddenFields: FiasFnsFields[];
}

export const FiasFnsManualEditor = ({
    value,
    onValueChange,
    withSteads,
    withIndex,
    abbreviationSize,
    withFederalTerritory,
    apiClient,
    extendedFields,
    hiddenFields,
}: FiasFnsManualEditorProps) => {
    useSendMetricsOnChange(value);
    useTrackFiasModalEnter("fns");

    const onChange = (control: Control) => (next: string) => {
        const path = getPath(control);
        onValueChange({ ...value, [path]: next });
    };

    const onBuildingTypeChange = (control: Control) => (next: string) => {
        const shortened = abbreviationSize === "short";
        const nextValue = shortened ? getHouseTypeShortName(next) : next;
        onChange(control)(nextValue ?? "");
    };

    const hiddenRows = useHiddenRows(hiddenFields);
    const steadsVisible = withSteads && !hiddenRows.Stead;

    const guidInExtendedFields = useMemo(
        () => extendedFields.some(({ type }) => type === FiasExtendFieldsType.guid),
        extendedFields
    );
    const guidVisible = guidInExtendedFields && !hiddenRows.Id;

    const pathsFromFields = usePathsFromFiasFields(extendedFields);
    const getPath = (control: Control): string => pathsFromFields[control] || defaultPaths[control];
    const getValue = (control: Control) => value[getPath(control)] ?? "";

    const fullSizeAbbreviation = abbreviationSize === "long";

    const skipIndexValidation: boolean = useMemo(() => {
        const index = extendedFields.find(({ type }) => type === FiasExtendFieldsType.index);
        return Boolean(index?.skipDefaultValidation);
    }, extendedFields);

    return (
        <Root>
            <If condition={withIndex}>
                <Index
                    path={getPath(Control.ZipCode)}
                    skipDefaultValidation={skipIndexValidation}
                    value={getValue(Control.ZipCode)}
                    onValueChange={onChange(Control.ZipCode)}
                />
            </If>
            <If condition={!hiddenRows.Region}>
                <Region
                    value={getValue(Control.Region)}
                    onValueChange={onChange(Control.Region)}
                    apiClient={apiClient}
                />
            </If>
            <If condition={!hiddenRows.District}>
                <District withFederalTerritory={withFederalTerritory} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={!hiddenRows.Settlement}>
                <Settlement getValue={getValue} onChange={onChange} />
            </If>
            <If condition={!hiddenRows.City}>
                <City fullSizeAbbreviation={fullSizeAbbreviation} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={!hiddenRows.Structure}>
                <Structure fullSizeAbbreviation={fullSizeAbbreviation} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={!hiddenRows.Street}>
                <Street fullSizeAbbreviation={fullSizeAbbreviation} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={steadsVisible}>
                <Stead value={getValue(Control.Stead)} onValueChange={onChange(Control.Stead)} />
            </If>
            <If condition={!hiddenRows.House}>
                <House
                    type={getValue(Control.HouseType)}
                    number={getValue(Control.HouseNumber)}
                    onTypeChange={onBuildingTypeChange(Control.HouseType)}
                    onNumberChange={onChange(Control.HouseNumber)}
                />
            </If>
            <If condition={!hiddenRows.Housing}>
                <Housing
                    type={getValue(Control.HousingType)}
                    number={getValue(Control.HousingNumber)}
                    onTypeChange={onBuildingTypeChange(Control.HousingType)}
                    onNumberChange={onChange(Control.HousingNumber)}
                />
            </If>
            <If condition={!hiddenRows.Building}>
                <Building
                    type={getValue(Control.BuildingType)}
                    number={getValue(Control.BuildingNumber)}
                    onTypeChange={onBuildingTypeChange(Control.BuildingType)}
                    onNumberChange={onChange(Control.BuildingNumber)}
                />
            </If>
            <If condition={!hiddenRows.Apartment}>
                <Apartment fullSizeAbbreviation={fullSizeAbbreviation} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={!hiddenRows.Room}>
                <Room fullSizeAbbreviation={fullSizeAbbreviation} getValue={getValue} onChange={onChange} />
            </If>
            <If condition={guidVisible}>
                <Guid
                    path={getPath(Control.Id)}
                    value={value}
                    onValueChange={onValueChange}
                    extendedFields={extendedFields}
                    apiClient={apiClient}
                />
            </If>
        </Root>
    );
};

const usePathsFromFiasFields = (extendedFields: ParsedExtendedFiasField[]) =>
    useMemo(() => {
        const emptyCollection = {} as Record<Control, string>;
        return extendedFields.reduce(collectFieldPath, emptyCollection);
    }, extendedFields);

const collectFieldPath = (collection: Record<Control, string>, field: ParsedExtendedFiasField) => {
    const source = field.sourceValue.find(isControlApiPath);

    if (source) {
        const apiPath = source.fiasApiPath;
        const control = controlsApiPaths[apiPath];

        collection[control] = field.path;
    } else if (field.type === FiasExtendFieldsType.guid) {
        collection[Control.Id] = field.path;
    }

    return collection;
};

interface ControlSourceValue extends Omit<FiasSourceValue, "fiasApiPath"> {
    fiasApiPath: ControlApiPath;
}

const isControlApiPath = (sourceValue: FiasSourceValue): sourceValue is ControlSourceValue => {
    const apiPath = sourceValue.fiasApiPath;
    return !!apiPath && apiPath in controlsApiPaths;
};

const useSendMetricsOnChange = (value: FiasAddress) => {
    const [isChanged, setIsChanged] = useState<boolean>(false);
    const [metricSent, setMetricSent] = useState<boolean>(false);
    const track = useMetrics();

    useEffect(() => {
        if (isChanged && !metricSent) {
            track(FIAS_HANDLE_EDIT_MODE_VALUE_CHANGE);
            setMetricSent(true);
        }
        setIsChanged(true);
    }, dirtyReduxHack(value));
};

const useHiddenRows = (hiddenFields: FiasFnsFields[]) =>
    useMemo(() => {
        const emptyCollection = {} as Record<FiasFnsFields, boolean>;
        return hiddenFields.reduce((acc, cur) => {
            acc[cur] = true;
            return acc;
        }, emptyCollection);
    }, hiddenFields);
